import { FileText, File, FileVideo, FileAudio, FileCode, FileSpreadsheet, Play, Loader2 } from "lucide-react";
import { useState, useMemo, useEffect, useRef } from "react";
import { cn } from "@/modern/utils";
import ExcelViewer, { ExcelViewerProps } from "./excel-viewer";
import PdfViewer, { PdfViewerProps } from "./pdf-viewer";
import WordViewer, { WordViewerProps } from "./word-viewer";
import { getPresignedImageUrlForFile, getPresignedImageUrlForUnassignedFile } from "@/network/network";
import { SwivuFile } from "@/react/components/entityViews/files/types/swivuFile";
import { getFileExtension } from "@/modern/utils";
interface FilePreviewProps {
  file: SwivuFile;
  className?: string;
  src?: string;
  showName?: boolean;
  openFileOnClick?: boolean;
  fileIconClassName?: string;
  fileIconContainerClassName?: string;
  disableLoadingState?: boolean; // Disable internal loading animation
  enableVideoAudioRendering?: boolean; // Optional: Enable HTML video/audio tag rendering
  isThumbnail?: boolean; // Whether to render PDF in thumbnail mode
  isSwivuUnAssignedFile?: boolean; // Whether this is an unassigned Swivu file
  isUseFileQueryParam?: boolean;
  searchText?: string | null; // Optional: Search text to highlight in PDF viewer
}

const FilePreview = ({
  file,
  className = "",
  src,
  showName = false,
  openFileOnClick = false,
  fileIconClassName = "",
  fileIconContainerClassName = "",
  disableLoadingState = false,
  enableVideoAudioRendering = false,
  isThumbnail = false,
  isSwivuUnAssignedFile = false,
  isUseFileQueryParam: _isUseFileQueryParam = false,
  searchText,
  ...props
}: FilePreviewProps & React.HTMLAttributes<HTMLDivElement>) => {
  const [isLoading, setIsLoading] = useState(!disableLoadingState);

  const getFileIcon = (filename?: string) => {
    const ext = getFileExtension(filename);
    const iconCls = cn("size-12", fileIconClassName);
    // Image files
    if (["jpg", "jpeg", "png", "gif", "bmp", "svg", "webp", "ico"].includes(ext)) {
      return null; // Show actual image
    }

    // Document files
    if (["pdf"].includes(ext)) {
      return <FileText className={iconCls} />;
    }

    if (["doc", "docx", "odt", "rtf", "txt", "md"].includes(ext)) {
      return <FileText className={iconCls} />;
    }

    // Spreadsheet files
    if (["xls", "xlsx", "csv", "ods"].includes(ext)) {
      return <FileSpreadsheet className={iconCls} />;
    }

    // Presentation files
    if (["ppt", "pptx", "odp"].includes(ext)) {
      return <FileText className={iconCls} />;
    }

    // Video files
    if (["mp4", "avi", "mov", "wmv", "flv", "mkv", "webm", "m4v"].includes(ext)) {
      return <FileVideo className={iconCls} />;
    }

    // Audio files
    if (["mp3", "wav", "ogg", "m4a", "flac", "aac", "wma"].includes(ext)) {
      return <FileAudio className={iconCls} />;
    }

    // Code files
    if (["js", "jsx", "ts", "tsx", "html", "css", "scss", "json", "xml", "py", "java", "c", "cpp", "h", "cs", "php", "rb", "go", "rs", "swift", "kt"].includes(ext)) {
      return <FileCode className={iconCls} />;
    }

    // Archive files
    if (["zip", "rar", "7z", "tar", "gz", "bz2", "xz"].includes(ext)) {
      return <File className={iconCls} />;
    }

    // Default file icon
    return <File className={iconCls} />;
  };

  const icon = getFileIcon(file.name);
  const isImage = !icon;

  const handleFileClick = () => {
    if (!openFileOnClick) return;
    const fileUrl = file.url || file.thumbnailUrl || src;
    if (fileUrl) {
      window.open(fileUrl, "_blank");
    }
  };
  // Check if file is video or audio when rendering is enabled (memoized)
  const { isVideo, isAudio, isPdf, isWordDoc, isExcel, fileUrl } = useMemo(() => {
    const extension = getFileExtension(file.name);
    const isVideoExt = ["mp4", "avi", "mov", "wmv", "flv", "mkv", "webm", "m4v"].includes(extension);
    const isAudioExt = ["mp3", "wav", "ogg", "m4a", "flac", "aac", "wma"].includes(extension);
    // Thumbnail grids need a video frame without full player controls.
    const video = isVideoExt && (enableVideoAudioRendering || isThumbnail);
    const audio = isAudioExt && enableVideoAudioRendering;
    const isPdf = ["pdf"].includes(extension);
    const isWordDoc = ["docx"].includes(extension);
    const isExcel = ["xlsx", "xls"].includes(extension);
    // Prefer caller `src`; list file.url is not a media src. Video/audio self-presign below when missing.
    const url = src || undefined;
    return { ext: extension, isVideo: video, isAudio: audio, isPdf, isWordDoc, isExcel, fileUrl: url };
  }, [file, src, enableVideoAudioRendering, isThumbnail]);

  if (isWordDoc && !isThumbnail) {
    return (
      <>
        {!disableLoadingState && isLoading && <div className="bg-muted/50 animate-pulse rounded-md border w-full aspect-video absolute inset-0 z-10" />}
        <SwivuWordPreviewWrapper
          previewFileObject={file}
          setLoadingState={setIsLoading}
          fileUrl={fileUrl ?? ""}
          fileName={file.name}
          showName={showName}
          className={className}
          isThumbnail={isThumbnail}
          onLoadSuccess={() => {
            if (!disableLoadingState) {
              setIsLoading(false);
            }
          }}
          onLoadError={() => {
            if (!disableLoadingState) {
              setIsLoading(false);
            }
          }}
        />
      </>
    );
  }

  if (isExcel) {
    return (
      <>
        {!disableLoadingState && isLoading && <div className="bg-muted/50 animate-pulse rounded-md border w-full aspect-video absolute inset-0 z-10" />}
        <SwivuExcelPreviewWrapper
          previewFileObject={file}
          setLoadingState={setIsLoading}
          fileUrl={fileUrl ?? ""}
          fileName={file.name}
          showName={showName}
          className={className}
          isThumbnail={isThumbnail}
          isSwivuUnAssignedFile={isSwivuUnAssignedFile}
          onLoadSuccess={() => {
            if (!disableLoadingState) {
              setIsLoading(false);
            }
          }}
          onLoadError={() => {
            if (!disableLoadingState) {
              setIsLoading(false);
            }
          }}
        />
      </>
    );
  }

  if (isPdf) {
    return (
      <>
        {!disableLoadingState && isLoading && <div className="bg-muted/50 animate-pulse rounded-md border w-full aspect-video absolute inset-0 z-10" />}
        <SwivuPDFPreviewWrapper
          previewFileObject={file}
          setLoadingState={setIsLoading}
          fileUrl={fileUrl ?? ""}
          fileName={file.name}
          showName={showName}
          className={className}
          isThumbnail={isThumbnail}
          isSwivuUnAssignedFile={isSwivuUnAssignedFile}
          searchText={searchText}
          onLoadSuccess={() => {
            if (!disableLoadingState) {
              setIsLoading(false);
            }
          }}
          onLoadError={() => {
            if (!disableLoadingState) {
              setIsLoading(false);
            }
          }}
        />
      </>
    );
  }

  // Same as PDF/Word: decide type from file.name, then resolve a playable URL if needed.
  if (isVideo || isAudio) {
    return (
      <SwivuMediaPreviewWrapper
        previewFileObject={file}
        setLoadingState={setIsLoading}
        providedUrl={fileUrl}
        isVideo={isVideo}
        isThumbnail={isThumbnail}
        showName={showName}
        className={className}
        disableLoadingState={disableLoadingState}
        isSwivuUnAssignedFile={isSwivuUnAssignedFile}
        {...props}
      />
    );
  }

  if (isImage) {
    const imageSrc = src || undefined;
    if (!imageSrc) {
      return (
        <div
          className={cn("flex flex-col items-center justify-center text-foreground opacity-50", fileIconContainerClassName, className)}
          onClick={handleFileClick}
          {...props}
        >
          {getFileIcon(file.name) ?? <File className={cn("size-12", fileIconClassName)} />}
          {showName && file?.name && <span className="text-sm text-center mt-2 px-2 break-words max-w-full">{file.name}</span>}
        </div>
      );
    }

    return (
      <>
        {!disableLoadingState && isLoading && <div className="bg-muted/50 animate-pulse rounded-md border w-full aspect-video" />}
        {/* Use the URL as returned (presigned query strings must not be mutated). */}
        <img
          key={imageSrc}
          src={imageSrc}
          alt={file.name}
          className={cn("rounded-md", className, !disableLoadingState && isLoading && " hidden ", "cursor-pointer")}
          onClick={handleFileClick}
          onLoad={() => {
            if (!disableLoadingState) {
              setIsLoading(false);
            }
          }}
          onError={(e) => {
            if (!disableLoadingState) {
              setIsLoading(false);
            }
            // On error, replace with file icon
            const target = e.target as HTMLImageElement;
            target.style.display = "none";
            const iconContainer = document.createElement("div");
            iconContainer.className = cn("w-full h-full flex flex-col items-center justify-center opacity-50 cursor-pointer ", fileIconContainerClassName);
            iconContainer.innerHTML = `<svg class="size-12 text-foreground ${fileIconClassName}" xmlns="http://www.w3.org/2000/svg"  viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2v4a2 2 0 0 0 2 2h4"/><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/></svg>`;
            iconContainer.onclick = handleFileClick;

            if (showName) {
              const nameContainer = document.createElement("div");
              nameContainer.className = cn("text-sm text-center mt-2 px-2 break-words max-w-full");
              nameContainer.innerHTML = file.name || "";
              iconContainer.appendChild(nameContainer);
            }

            target.parentElement?.appendChild(iconContainer);
          }}
          {...props}
        />
      </>
    );
  }
  return (
    <div
      className={`flex flex-col items-center justify-center text-foreground ${!isImage && ` opacity-50 ${fileIconContainerClassName}`} ${className} cursor-pointer`}
      onClick={handleFileClick}
      {...props}
    >
      {icon}
      {!isImage && showName && file?.name && <span className="text-sm text-center mt-2 px-2 break-words max-w-full">{file.name}</span>}
    </div>
  );
};

const getFileURLAfterPresignedCheck = async ({file, isSwivuUnAssignedFile = false}: {file: SwivuFile; isSwivuUnAssignedFile?: boolean}): Promise<string | null> => {
  try {
    const response = isSwivuUnAssignedFile
      ? await getPresignedImageUrlForUnassignedFile(file.id)
      : await getPresignedImageUrlForFile(file.id);
    return response.data.url;
  } catch (error) {
    console.error("[getFileURLAfterPresignedCheck] Something went wrong when getting file presigned URL", error);
    return null;
  }
}

const usePresignedPreviewUrl = (
  previewFileObject: SwivuFile,
  setLoadingState: (loading: boolean) => void,
  isSwivuUnAssignedFile?: boolean,
) => {
  const [fileUrl, setFileUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getFileURLAfterPresignedCheck({ file: previewFileObject, isSwivuUnAssignedFile })
      .then((url) => {
        if (!cancelled) setFileUrl(url);
      })
      .catch(() => {
        if (!cancelled) {
          setLoadingState(false);
          setFileUrl(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [previewFileObject, setLoadingState, isSwivuUnAssignedFile]);

  return fileUrl;
};

type SwivuPDFPreviewWrapperProps = PdfViewerProps & {
  previewFileObject: SwivuFile;
  setLoadingState: (loading: boolean) => void;
  isSwivuUnAssignedFile?: boolean;
  searchText?: string | null;
};

const SwivuPDFPreviewWrapper = ({ previewFileObject, setLoadingState, isSwivuUnAssignedFile = false, searchText, ...props }: SwivuPDFPreviewWrapperProps) => {
  const fileUrl = usePresignedPreviewUrl(previewFileObject, setLoadingState, isSwivuUnAssignedFile);
  if (!fileUrl) return null;
  return <PdfViewer {...props} fileUrl={fileUrl} searchText={searchText} />
};

type SwivuWordPreviewWrapperProps = WordViewerProps & {
  previewFileObject: SwivuFile;
  setLoadingState: (loading: boolean) => void;
  isSwivuUnAssignedFile?: boolean;
};

const SwivuWordPreviewWrapper = ({ previewFileObject, isSwivuUnAssignedFile, setLoadingState, ...props }: SwivuWordPreviewWrapperProps) => {
  const fileUrl = usePresignedPreviewUrl(previewFileObject, setLoadingState, isSwivuUnAssignedFile);
  if (!fileUrl) return null;
  return <WordViewer {...props} fileUrl={fileUrl} />
};

type SwivuExcelPreviewWrapperProps = ExcelViewerProps & {
  previewFileObject: SwivuFile;
  setLoadingState: (loading: boolean) => void;
  isSwivuUnAssignedFile?: boolean;
};

const SwivuExcelPreviewWrapper = ({ previewFileObject, isSwivuUnAssignedFile, setLoadingState, ...props }: SwivuExcelPreviewWrapperProps) => {
  const fileUrl = usePresignedPreviewUrl(previewFileObject, setLoadingState, isSwivuUnAssignedFile);
  if (!fileUrl) return null;
  return <ExcelViewer {...props} fileUrl={fileUrl} />
};

type SwivuMediaPreviewWrapperProps = {
  previewFileObject: SwivuFile;
  setLoadingState: (loading: boolean) => void;
  providedUrl?: string;
  isVideo: boolean;
  isThumbnail?: boolean;
  showName?: boolean;
  className?: string;
  disableLoadingState?: boolean;
  isSwivuUnAssignedFile?: boolean;
} & React.HTMLAttributes<HTMLDivElement>;

const looksLikeImageUrl = (url: string): boolean =>
  /\.(jpe?g|png|gif|webp|bmp|svg)(\?|#|$)/i.test(url);

/** Subtle corner cue so video thumbs still read as video. */
const VideoThumbnailPlayBadge = () => (
  <div
    className="pointer-events-none absolute bottom-1.5 right-1.5 z-[1] flex size-6 items-center justify-center rounded-full bg-black/60 shadow-sm ring-1 ring-white/20"
    aria-hidden
  >
    <Play className="size-2.5 translate-x-px fill-white text-white" />
  </div>
);

/** Resolves a playable URL (caller `src` or presign), same pattern as PDF/Word wrappers. */
const SwivuMediaPreviewWrapper = ({
  previewFileObject,
  setLoadingState,
  providedUrl,
  isVideo,
  isThumbnail = false,
  showName = false,
  className = "",
  disableLoadingState = false,
  isSwivuUnAssignedFile = false,
  ...props
}: SwivuMediaPreviewWrapperProps) => {
  const [fileUrl, setFileUrl] = useState<string | null>(providedUrl ?? null);
  const [thumbReady, setThumbReady] = useState(false);
  const thumbVideoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (providedUrl) {
      setFileUrl(providedUrl);
      return;
    }

    let cancelled = false;
    getFileURLAfterPresignedCheck({ file: previewFileObject, isSwivuUnAssignedFile })
      .then((url) => {
        if (!cancelled) setFileUrl(url);
      })
      .catch(() => {
        if (!cancelled) {
          setLoadingState(false);
          setFileUrl(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [providedUrl, previewFileObject, setLoadingState, isSwivuUnAssignedFile]);

  // Seek slightly past 0 so browsers that only paint black at t=0 still show a frame.
  useEffect(() => {
    if (!isThumbnail || !isVideo || !fileUrl || looksLikeImageUrl(fileUrl)) return;
    const video = thumbVideoRef.current;
    if (!video) return;

    setThumbReady(false);

    const markReady = () => setThumbReady(true);

    const showFrame = () => {
      try {
        if (video.currentTime < 0.05 && Number.isFinite(video.duration) && video.duration > 0.15) {
          video.currentTime = 0.1;
        } else {
          markReady();
        }
      } catch {
        markReady();
      }
    };

    const onError = () => setThumbReady(false);

    video.addEventListener("loadeddata", showFrame);
    video.addEventListener("seeked", markReady);
    video.addEventListener("error", onError);
    if (video.readyState >= 2) showFrame();

    return () => {
      video.removeEventListener("loadeddata", showFrame);
      video.removeEventListener("seeked", markReady);
      video.removeEventListener("error", onError);
    };
  }, [fileUrl, isThumbnail, isVideo]);

  if (!fileUrl) return null;

  if (isVideo && isThumbnail) {
    // Server may return a generated still; otherwise show the first video frame.
    if (looksLikeImageUrl(fileUrl)) {
      return (
        <div className={cn("relative size-full min-h-0 min-w-0 overflow-hidden bg-muted", className)}>
          <img
            src={fileUrl}
            alt={previewFileObject.name}
            className="absolute inset-0 size-full object-cover"
          />
          <VideoThumbnailPlayBadge />
        </div>
      );
    }

    return (
      <div className={cn("relative size-full min-h-0 min-w-0 overflow-hidden bg-muted", className)}>
        {!thumbReady && (
          <div className="absolute inset-0 z-[1] flex items-center justify-center bg-muted">
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
          </div>
        )}
        <video
          ref={thumbVideoRef}
          src={fileUrl}
          muted
          playsInline
          preload="auto"
          className={cn(
            "pointer-events-none absolute inset-0 size-full object-cover transition-opacity duration-200",
            thumbReady ? "opacity-100" : "opacity-0",
          )}
          aria-label={previewFileObject.name}
        />
        {thumbReady && <VideoThumbnailPlayBadge />}
      </div>
    );
  }

  if (isVideo) {
    return (
      <div className={cn("flex flex-col items-center justify-center", className)} {...props}>
        <video
          src={fileUrl}
          controls
          className="max-h-full max-w-full rounded-md aspect-video object-contain"
          onLoadStart={() => {
            if (!disableLoadingState) {
              setLoadingState(false);
            }
          }}
        >
          Your browser does not support the video tag.
        </video>
        {showName && previewFileObject?.name && (
          <span className="text-sm text-center mt-4 px-2 break-words max-w-full">{previewFileObject.name}</span>
        )}
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col items-center justify-center", className)} {...props}>
      <audio
        src={fileUrl}
        controls
        className="max-w-full"
        onLoadStart={() => {
          if (!disableLoadingState) {
            setLoadingState(false);
          }
        }}
      >
        Your browser does not support the audio tag.
      </audio>
      {showName && previewFileObject?.name && (
        <span className="text-sm text-center mt-2 px-2 break-words max-w-full">{previewFileObject.name}</span>
      )}
    </div>
  );
};

export default FilePreview;
